import React, { useState } from "react";

const CCNP = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Master advanced networking concepts, prepare for the Cisco Certified Network Professional (CCNP) Enterprise certification, focusing on the core exam (ENCOR 350-401) and a concentration exam (ENARSI 300-410), and gain hands-on skills in enterprise network design, implementation, and troubleshooting.
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per day.
          <p className="font-bold text-sm">Prerequisites:</p> CCNA certification or equivalent knowledge; experience with Cisco devices and networking fundamentals (routing, switching, IP addressing).
          <p className="font-bold text-sm">Tools Needed:</p> A computer with Cisco Packet Tracer, GNS3, access to physical Cisco devices (if available), and internet access.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to CCNP Enterprise",
          objective: "Understand CCNP certification and its exams.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of CCNP Enterprise certification.</li>
                <li>Understanding ENCOR 350-401 and ENARSI 300-410 exams.</li>
                <li>Setting up a lab environment (Packet Tracer, GNS3).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install and configure GNS3 for advanced simulations.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Enterprise Network Architecture",
          objective: "Learn enterprise network design principles.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Cisco enterprise architecture (access, distribution, core).</li>
                <li>High availability and redundancy concepts.</li>
                <li>Designing a hierarchical network.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Design a three-tier network topology in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Advanced Switching – VLANs and VTP",
          objective: "Deepen VLAN and VTP knowledge.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Advanced VLAN configurations (private VLANs).</li>
                <li>VLAN Trunking Protocol (VTP) setup and modes.</li>
                <li>Troubleshooting VLAN and VTP issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure VTP and private VLANs in a network.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Advanced Switching – EtherChannel",
          objective: "Learn EtherChannel for link aggregation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>EtherChannel concepts and benefits.</li>
                <li>Configuring LACP and PAgP.</li>
                <li>Verifying and troubleshooting EtherChannel.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure EtherChannel between switches.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Advanced Switching – STP Enhancements",
          objective: "Learn STP enhancements for redundancy.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Spanning Tree Protocol (STP) review.</li>
                <li>Rapid Per-VLAN Spanning Tree (RPVST+).</li>
                <li>STP features (PortFast, BPDU Guard).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure RPVST+ and enable PortFast.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Advanced Routing – OSPF Part 1",
          objective: "Learn advanced OSPF configurations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>OSPF review and multi-area OSPF.</li>
                <li>OSPF area types (stub, NSSA).</li>
                <li>Configuring OSPF authentication.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure multi-area OSPF with a stub area.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Advanced Routing – OSPF Part 2",
          objective: "Deepen OSPF knowledge with optimization.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>OSPF route summarization.</li>
                <li>OSPF virtual links.</li>
                <li>Troubleshooting OSPF adjacency issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure OSPF route summarization and virtual links.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Advanced Routing – BGP Part 1",
          objective: "Learn BGP fundamentals.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to BGP (eBGP vs. iBGP).</li>
                <li>Configuring BGP neighbors.</li>
                <li>Understanding BGP attributes (AS path, next-hop).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure eBGP between two routers.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Advanced Routing – BGP Part 2",
          objective: "Deepen BGP knowledge with advanced features.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>BGP route filtering and path selection.</li>
                <li>Configuring BGP route maps.</li>
                <li>Troubleshooting BGP connectivity.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Apply BGP route filtering using a route map.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: IP Services – HSRP",
          objective: "Learn High Availability with HSRP.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding First Hop Redundancy Protocols (FHRP).</li>
                <li>Configuring Hot Standby Router Protocol (HSRP).</li>
                <li>Verifying HSRP operation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure HSRP on two routers.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: IP Services – VRRP and GLBP",
          objective: "Learn additional FHRP protocols.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Virtual Router Redundancy Protocol (VRRP).</li>
                <li>Gateway Load Balancing Protocol (GLBP).</li>
                <li>Comparing HSRP, VRRP, and GLBP.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure VRRP and GLBP in a network.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Advanced NAT Configurations",
          objective: "Learn advanced NAT techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Static NAT with port mapping.</li>
                <li>Dynamic NAT with pools.</li>
                <li>Troubleshooting NAT issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure static NAT with port mapping.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: QoS Fundamentals",
          objective: "Learn Quality of Service (QoS) concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to QoS and its importance.</li>
                <li>Classification and marking (DSCP, CoS).</li>
                <li>Configuring basic QoS policies.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure QoS marking on a router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: QoS – Traffic Shaping and Policing",
          objective: "Learn advanced QoS techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Traffic shaping vs. policing.</li>
                <li>Configuring shaping and policing policies.</li>
                <li>Verifying QoS configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure traffic shaping on a router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Wireless Networking – Advanced",
          objective: "Learn advanced wireless concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Wireless LAN controller (WLC) basics.</li>
                <li>Configuring WLAN security (WPA3).</li>
                <li>Wireless roaming and mobility.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a WLAN with WPA3 security.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Network Security – AAA",
          objective: "Learn AAA for network security.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding AAA (Authentication, Authorization, Accounting).</li>
                <li>Configuring AAA with RADIUS and TACACS+.</li>
                <li>Verifying AAA operations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure AAA with TACACS+ on a router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Network Security – ACLs and CoPP",
          objective: "Learn advanced security with ACLs and CoPP.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Advanced ACL configurations (time-based, reflexive).</li>
                <li>Control Plane Policing (CoPP) concepts.</li>
                <li>Configuring CoPP to protect the control plane.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a time-based ACL and CoPP policy.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: VPN Technologies – GRE",
          objective: "Learn GRE for VPN tunneling.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding Generic Routing Encapsulation (GRE).</li>
                <li>Configuring a GRE tunnel.</li>
                <li>Verifying GRE tunnel operation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a GRE tunnel between two routers.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: VPN Technologies – IPsec",
          objective: "Learn IPsec for secure VPNs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>IPsec concepts (IKE, ESP, AH).</li>
                <li>Configuring IPsec over GRE.</li>
                <li>Troubleshooting IPsec VPNs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure IPsec to secure a GRE tunnel.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Network Automation – Basics",
          objective: "Learn network automation fundamentals.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to network automation.</li>
                <li>Using Python for network automation.</li>
                <li>Introduction to NETCONF and RESTCONF.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a Python script to SSH into a Cisco device.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Network Automation – Ansible",
          objective: "Learn Ansible for network automation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Ansible for networking.</li>
                <li>Writing Ansible playbooks for Cisco devices.</li>
                <li>Automating VLAN configuration with Ansible.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Automate VLAN creation using Ansible.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: SDN and Cisco DNA",
          objective: "Learn Software-Defined Networking (SDN).",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to SDN and Cisco DNA Center.</li>
                <li>SDN architecture and components.</li>
                <li>Exploring Cisco DNA Center features.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Simulate Cisco DNA Center in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Troubleshooting – Methodology",
          objective: "Learn structured troubleshooting.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshooting methodologies (bottom-up, top-down).</li>
                <li>Using debug and show commands effectively.</li>
                <li>Documenting troubleshooting steps.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot a simulated network issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Troubleshooting – Routing Issues",
          objective: "Learn to troubleshoot routing problems.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Common OSPF and BGP issues.</li>
                <li>Debugging routing protocols.</li>
                <li>Fixing routing loops and black holes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot an OSPF adjacency issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Troubleshooting – Security and VPN",
          objective: "Learn to troubleshoot security and VPN issues.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing ACL and AAA issues.</li>
                <li>Troubleshooting IPsec VPN connectivity.</li>
                <li>Verifying CoPP policies.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot an IPsec VPN tunnel failure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., Enterprise Network Design).</li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – Development (Part 1)",
          objective: "Develop the initial setup of the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Designing the network topology (three-tier).</li>
                <li>Configuring VLANs, EtherChannel, and HSRP.</li>
                <li>Setting up multi-area OSPF.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start building the project infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – Development (Part 2)",
          objective: "Continue developing the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring BGP and IPsec VPN.</li>
                <li>Implementing QoS and AAA security.</li>
                <li>Testing network connectivity.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhance the project with advanced features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Development (Part 3)",
          objective: "Add automation and finalize configurations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Automating configurations with Ansible.</li>
                <li>Optimizing QoS and security settings.</li>
                <li>Verifying high availability (HSRP, BGP).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Automate part of the project using Ansible.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Testing and Debugging",
          objective: "Test and debug the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Testing network connectivity and redundancy.</li>
                <li>Troubleshooting routing, VPN, and security issues.</li>
                <li>Optimizing configurations for performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and fix issues in the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Final Project – Documentation",
          objective: "Document the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a project README.</li>
                <li>Documenting configurations and topology.</li>
                <li>Preparing a presentation outline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write documentation for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Preparing for ENCOR – Network Design",
          objective: "Review network design for ENCOR exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review enterprise network design principles.</li>
                <li>Practice high availability (HSRP, EtherChannel).</li>
                <li>Mock scenarios for network design.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a redundant network with HSRP.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Preparing for ENCOR – Advanced Routing",
          objective: "Review advanced routing for ENCOR exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review OSPF and BGP configurations.</li>
                <li>Practice route summarization and filtering.</li>
                <li>Mock routing scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure BGP with route filtering.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Preparing for ENCOR – Security and Automation",
          objective: "Review security and automation for ENCOR exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review AAA, ACLs, and IPsec VPNs.</li>
                <li>Practice automation with Ansible and Python.</li>
                <li>Mock security and automation scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Automate a VLAN configuration with Ansible.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Preparing for ENARSI – Troubleshooting Basics",
          objective: "Learn troubleshooting for ENARSI exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review troubleshooting methodologies.</li>
                <li>Practice using debug and show commands.</li>
                <li>Mock troubleshooting scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot a simulated network issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Preparing for ENARSI – Routing Issues",
          objective: "Troubleshoot advanced routing issues.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing OSPF and BGP issues.</li>
                <li>Fixing route redistribution problems.</li>
                <li>Mock routing troubleshooting scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot a BGP route advertisement issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Preparing for ENARSI – VPN and Security",
          objective: "Troubleshoot VPN and security issues.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing IPsec and GRE issues.</li>
                <li>Troubleshooting AAA and CoPP policies.</li>
                <li>Mock VPN and security troubleshooting scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot an IPsec VPN failure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – Peer Review",
          objective: "Receive feedback on the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Presenting to peers for feedback.</li>
                <li>Discussing improvements and optimizations.</li>
                <li>Finalizing the project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present and refine the project based on feedback.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Final Project – Presentation Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practicing the full demo.</li>
                <li>Anticipating questions from the audience.</li>
                <li>Refining the presentation flow.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the presentation with a timer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Delivering the project demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Receiving feedback from peers and instructor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Exam Strategy – ENCOR",
          objective: "Learn strategies for the ENCOR exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding ENCOR 350-401 exam format.</li>
                <li>Time management and prioritization tips.</li>
                <li>Reviewing key topics (routing, security, automation).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice a timed mock ENCOR scenario.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Exam Strategy – ENARSI",
          objective: "Learn strategies for the ENARSI exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding ENARSI 300-410 exam format.</li>
                <li>Time management and troubleshooting tips.</li>
                <li>Reviewing key troubleshooting areas.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice a timed mock ENARSI scenario.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Mock Exam – ENCOR",
          objective: "Practice for the ENCOR exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Design and configure a network.</li>
                <li>Implement routing, security, and automation.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete a mock ENCOR task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Mock Exam – ENARSI",
          objective: "Practice for the ENARSI exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Troubleshoot a complex network.</li>
                <li>Diagnose routing, VPN, and security issues.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete a mock ENARSI troubleshooting task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Course Review and Next Steps",
          objective: "Review course content and plan future learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Recap of key CCNP concepts.</li>
                <li>Preparing for CCNP certification exams.</li>
                <li>Exploring advanced certifications (e.g., CCIE).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Discuss personal projects and career paths.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Design and implement enterprise network architectures.</li>
            <li>Configure advanced routing (OSPF, BGP) and switching (VLANs, EtherChannel).</li>
            <li>Implement security, VPNs, QoS, and high availability (HSRP, VRRP).</li>
            <li>Automate network tasks using Python and Ansible.</li>
            <li>Troubleshoot complex network issues and prepare for CCNP exams.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Enterprise Network Design:</span> Design a three-tier network with OSPF, BGP, IPsec, QoS, and automation.
            </li>
            <li>
              <span className="font-bold text-sm">Secure Data Center Network:</span> Build a network with VLANs, HSRP, AAA, and CoPP for security.
            </li>
            <li>
              <span className="font-bold text-sm">Branch Office Connectivity:</span> Create a network with GRE over IPsec, BGP, and Ansible automation.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Practice with Cisco Packet Tracer and GNS3 for realistic simulations.</li>
            <li>Review CCNP ENCOR (350-401) and ENARSI (300-410) exam topics on Cisco’s official website.</li>
            <li>Use discussion time for troubleshooting complex scenarios and Q&A.</li>
          </ul>
          <p className="text-sm">
            This syllabus covers essential CCNP Enterprise topics over 45 days, preparing learners for certification and advanced networking roles.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-gray-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Cisco Certified Network Professional (CCNP) Enterprise Syllabus
        </h1>
        <p className="text-sm text-white">(45 Hours – 1 Hour Per Day)</p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CCNP;